/**
 * 🚀 Landing Page de Marketing - Smarty
 * 
 * Diseño moderno, minimalista y profesional
 * Página principal de marketing que se muestra en: smarty.lat
 */

import { useEffect, useState } from 'react';
import { useListaCacheada } from '@/hooks/useDatosCacheados';
import { Button } from '@/components/ui/button';
import { 
  ChefHat, 
  ShoppingCart, 
  BarChart3,
  Shield, 
  Clock,
  Package,
  Smartphone,
  ArrowRight,
  Store,
  Users,
  CheckCircle2,
  Sparkles,
  Zap
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { devLog } from '@/lib/utils';
import { buildUrl } from '@/lib/url-utils';
import { getWebHost, getRootDomain } from '@/config/app-domains.config';

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  logo_url: string | null;
  description: string | null;
}

interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  features: string[];
  limits: Record<string, unknown>;
  is_active: boolean;
  display_order: number;
}

// Filas crudas de Supabase (el cliente corre sin tipos generados, así que el tipo
// de la fila se declara a mano — patrón CategoryRow de CategoryEditPage).
interface TenantRow {
  id: string;
  name: string | null;
  slug: string | null;
}

interface PlanRow {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  features: unknown; // jsonb
  limits: Record<string, unknown> | null; // jsonb
  is_active: boolean;
  display_order: number;
}

export default function MarketingLandingPage() {

  // Por el camino pavimentado de la caché (ADR-0012, paso 3). Ver `useDatosCacheados`.
  //
  // 🪤 Son DOS consultas separadas, no una: cada bloque de la landing (restaurantes, planes) tiene
  // su propio estado de carga y se muestra apenas está listo. Unirlas haría que el más lento
  // retenga al más rápido — en una página pública, eso es visitante que se va.
  //
  // 🪤 Los errores se tragan a propósito y devuelven lista vacía: es una landing PÚBLICA. Un aviso
  // rojo de "no se pudieron cargar los planes" a un visitante que ni cuenta tiene no le sirve de
  // nada; que la sección no aparezca es el comportamiento correcto.
  const { datos: tenants, cargando: loading } = useListaCacheada<Tenant>({
    clave: ['landing-tenants'],
    frescura: 5 * 60_000,
    traer: async () => {
      const { data, error } = await supabase.from('tenants').select('id, name, slug').limit(6);
      if (error) { devLog.error('Error cargando tenants:', error); return []; }
      return (data || []).map((t: TenantRow) => ({
        id: t.id,
        name: t.name || 'Restaurante',
        subdomain: t.slug || '',
        logo_url: null,
        description: null,
      }));
    },
  });

  const { datos: plans, cargando: plansLoading } = useListaCacheada<Plan>({
    clave: ['landing-plans'],
    frescura: 5 * 60_000,
    traer: async () => {
      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (error) { devLog.error('Error cargando planes:', error); return []; }
      return (data || []).map((p: PlanRow) => ({
        id: p.id,
        name: p.name,
        slug: p.slug,
        description: p.description,
        price_monthly: p.price_monthly,
        price_yearly: p.price_yearly,
        // features es jsonb: solo se conservan las entradas string reales
        features: Array.isArray(p.features)
          ? p.features.filter((f): f is string => typeof f === 'string')
          : [],
        limits: p.limits || {},
        is_active: p.is_active,
        display_order: p.display_order,
      }));
    },
  });

  const handleVisitTenant = (subdomain: string) => {
    if (subdomain) {
      // .local en dev, .lat en prod
      window.location.href = buildUrl(`${subdomain}.${getRootDomain()}`);
    }
  };

  const handleGetStarted = () => {
    // Redirigir a página de registro (que crearemos)
    window.location.href = '/registro';
  };

  const handleLogin = () => {
    // Redirigir al panel de mercaderes (web) para login
    window.location.href = buildUrl(getWebHost(), '/login');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Minimalista */}
      <header className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                <ChefHat className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-gray-900">Smarty</span>
            </div>
            <Button 
              variant="ghost"
              className="text-gray-700 hover:text-gray-900"
              onClick={handleLogin}
            >
              Iniciar Sesión
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Minimalista y Moderno */}
      <section className="pt-32 pb-20 sm:pt-40 sm:pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-100 mb-8">
            <Sparkles className="h-4 w-4 text-red-600" />
            <span className="text-sm font-medium text-red-700">La plataforma todo-en-uno para restaurantes</span>
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight mb-6 leading-tight">
            Gestiona tu restaurante
            <span className="block text-red-600">como un profesional</span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed">
            Sistema POS, ecommerce, analíticas y más. Todo lo que necesitas para hacer crecer tu negocio en una sola plataforma.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-gray-900 text-white hover:bg-gray-800 h-14 px-8 text-base font-medium shadow-lg hover:shadow-xl transition-all"
              onClick={handleGetStarted}
            >
              Comenzar gratis
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-50 h-14 px-8 text-base font-medium"
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Ver características
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section - Grid Moderno */}
      <section id="features" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Todo lo que necesitas
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Una plataforma completa diseñada para restaurantes modernos
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: ShoppingCart,
                title: 'Punto de Venta',
                description: 'Sistema POS completo con gestión de mesas, pedidos y cobros en tiempo real',
              },
              {
                icon: Store,
                title: 'Tienda Online',
                description: 'Ecommerce personalizado para que tus clientes pidan desde cualquier lugar',
              },
              {
                icon: BarChart3,
                title: 'Analíticas Avanzadas',
                description: 'Reportes detallados y métricas en tiempo real para tomar mejores decisiones',
              },
              {
                icon: Smartphone,
                title: 'Multiplataforma',
                description: 'Accede desde cualquier dispositivo. Funciona offline y sincroniza automáticamente',
              },
              {
                icon: Users,
                title: 'Gestión de Clientes',
                description: 'Base de datos completa, historial de pedidos y programas de fidelidad',
              },
              {
                icon: Shield,
                title: 'Seguro y Confiable',
                description: 'Datos encriptados, respaldos automáticos y cumplimiento de normativas',
              },
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all group"
              >
                <div className="h-12 w-12 rounded-xl bg-red-50 flex items-center justify-center mb-6 group-hover:bg-red-100 transition-colors">
                  <feature.icon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section - Minimalista */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '100+', label: 'Restaurantes' },
              { value: '50K+', label: 'Pedidos/mes' },
              { value: '99.9%', label: 'Uptime' },
              { value: '24/7', label: 'Soporte' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 uppercase tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Restaurants Section - Grid Moderno */}
      {tenants.length > 0 && (
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Restaurantes que confían en nosotros
              </h2>
              <p className="text-lg text-gray-600">
                Descubre algunos de los restaurantes que usan Smarty
              </p>
            </div>
            
            {loading ? (
              <div className="text-center py-12">
                <Clock className="h-8 w-8 text-gray-400 mx-auto mb-4 animate-spin" />
                <p className="text-gray-600">Cargando restaurantes...</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {tenants.map((tenant) => (
                  <button
                    key={tenant.id}
                    onClick={() => handleVisitTenant(tenant.subdomain)}
                    className="group bg-white rounded-xl p-6 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all"
                  >
                    {tenant.logo_url ? (
                      <img
                        src={tenant.logo_url}
                        alt={tenant.name}
                        className="h-12 w-12 rounded-lg mb-3 mx-auto object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-red-100 to-red-50 flex items-center justify-center mb-3 mx-auto group-hover:from-red-200 group-hover:to-red-100 transition-colors">
                        <ChefHat className="h-6 w-6 text-red-600" />
                      </div>
                    )}
                    <h3 className="text-sm font-medium text-gray-900 text-center line-clamp-2">
                      {tenant.name}
                    </h3>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Pricing Section - Moderna */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Planes que se adaptan a ti
            </h2>
            <p className="text-lg text-gray-600">
              Elige el plan perfecto para tu restaurante
            </p>
          </div>

          {plansLoading ? (
            <div className="text-center py-12">
              <Clock className="h-8 w-8 text-gray-400 mx-auto mb-4 animate-spin" />
              <p className="text-gray-600">Cargando planes...</p>
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Planes próximamente</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {plans.map((plan) => {
                const isPopular = plan.slug === 'pro';
                const priceDisplay = plan.price_monthly === 0 
                  ? 'Personalizado' 
                  : `S/ ${plan.price_monthly}`;
                const periodDisplay = plan.price_monthly === 0 ? '' : '/mes';
                
                return (
                  <div
                    key={plan.id}
                    className={`relative rounded-2xl border-2 p-8 ${
                      isPopular
                        ? 'border-red-500 shadow-xl scale-105'
                        : 'border-gray-200 hover:border-gray-300'
                    } bg-white transition-all`}
                  >
                    {isPopular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <span className="bg-red-500 text-white text-xs font-semibold px-4 py-1 rounded-full">
                          Más Popular
                        </span>
                      </div>
                    )}
                    
                    <div className="text-center mb-8">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                      {plan.description && (
                        <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                      )}
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold text-gray-900">{priceDisplay}</span>
                        {periodDisplay && <span className="text-gray-600">{periodDisplay}</span>}
                      </div>
                      {plan.price_yearly && plan.price_yearly > 0 && (
                        <p className="text-sm text-gray-500 mt-2">
                          S/ {plan.price_yearly}/año (ahorra 2 meses)
                        </p>
                      )}
                    </div>

                    <ul className="space-y-4 mb-8">
                      {Array.isArray(plan.features) && plan.features.slice(0, 8).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className={`w-full h-12 ${
                        isPopular
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-gray-900 hover:bg-gray-800 text-white'
                      }`}
                      onClick={handleGetStarted}
                    >
                      {plan.price_monthly === 0 ? 'Contactar' : 'Comenzar'}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Final - Minimalista */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            ¿Listo para hacer crecer tu restaurante?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Únete a cientos de restaurantes que ya están transformando su negocio con Smarty
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-gray-900 hover:bg-gray-100 h-14 px-8 text-base font-medium shadow-lg"
              onClick={handleGetStarted}
            >
              Comenzar ahora
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer Minimalista */}
      <footer className="bg-white border-t border-gray-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                <ChefHat className="h-4 w-4 text-white" />
              </div>
              <span className="text-lg font-semibold text-gray-900">Smarty</span>
            </div>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Smarty. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
